import { Injectable } from '@angular/core';

import { DocumentService } from '../../commercial-documents/services/documents.service';
import {
	ColumnDefAndHeader,
	DatacenterType,
	ECALL_BY_BATCH_DATACENTER_TYPE,
	ECALL_BY_VOLUME_DATACENTER_TYPE
} from '../models/datacenter-aggrid-view';
import { DimensionPipe } from '../pipes/format-dimension.pipe';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';
import {
	ProductCharacteristicsTranslationService
} from '../services/product-characteristics-translation.services';
import { safe } from '../utils/safe';

import {
	ColDefWithFields
} from './column-type';
import {
	BatchDatacenterColumnDefinition, DEFAULT_VIEW_BATCHES_DATACENTER
} from './types/batches/batch-datacenter-configuration';
import {
	DEFAULT_VIEW_DOCUMENT_DATACENTER,
	DocumentDatacenterColumnDefinition
} from './types/commercial-documents/document-datacenter-configuration';
import {
	DEFAULT_VIEW_ECALL_BY_BATCH_DATACENTER,
	EcallByBatchDatacenterColumnDefinition
} from './types/ecall/ecall-by-batch-datacenter-configuration';
import {
	DEFAULT_VIEW_ECALL_BY_VOLUME_DATACENTER,
	EcallByVolumeDatacenterColumnDefinition
} from './types/ecall/ecall-by-volume-datacenter-configuration';
import {
	DEFAULT_VIEW_OFFER_DATACENTER,
	OfferDatacenterColumnDefinition
} from './types/offers/offer-datacenter-configuration';
import {
	DEFAULT_VIEW_ORDER_DATACENTER,
	OrderDatacenterColumnDefinition
} from './types/orders/order-datacenter-configuration';
import {
	DEFAULT_VIEW_ORGANIZATION_DATACENTER,
	OrganizationDatacenterColumnDefinition
} from './types/organizations/organization-datacenter-configuration';
import {
	DEFAULT_VIEW_QUALITY_DATACENTER,
	QualityDatacenterColumnDefinition
} from './types/quality-issues/quality-datacenter-configuration';
import {
	DEFAULT_VIEW_SCHEDULE_BUSINESS_ORDER_DATACENTER,
	ScheduleBusinessOrderDatacenterColumnDefinition
} from './types/schedule-business-orders/schedule-business-order-datacenter-configuration';
import {
	DEFAULT_VIEW_SHIPMENT_DATACENTER,
	ShipmentDatacenterColumnDefinition
} from './types/shipments/shipment-datacenter-configuration';
import {
	DEFAULT_VIEW_USERS_DATACENTER,
	UserDatacenterColumnDefinition
} from './types/users/users-datacenter-configuration';
import {
	DEFAULT_VIEW_AQDB_DATACENTER,
	AqdbDatacenterColumnDefinition
} from './types/aqdb/aqdb-datacenter-configuration';
import {
	ProductListColumnDefinition
} from './types/product-list/product-list-configuration';
import {
	DEFAULT_VIEW_PROTOTYPE_DATACENTER,
	PrototypeDatacenterColumnDefinition
} from './types/prototypes/prototype-datacenter-configuration';

@Injectable({ providedIn: 'root' })
export class DatacenterDefaultConfiguration {
	private ecallByBatchDatacenterColumnDef: EcallByBatchDatacenterColumnDefinition;
	private ecallByVolumeDatacenterColumnDef: EcallByVolumeDatacenterColumnDefinition;
	private orderDatacenterColumnDef: OrderDatacenterColumnDefinition;
	private batchDatacenterColumnDef: BatchDatacenterColumnDefinition;
	private shipmentDatacenterColumnDef: ShipmentDatacenterColumnDefinition;
	private qualityDatacenterColumnDef: QualityDatacenterColumnDefinition;
	private offerDatacenterColumnDef: OfferDatacenterColumnDefinition;
	private documentDatacenterColumnDef: DocumentDatacenterColumnDefinition;
	private scheduleBusinessOrderDatacenterColumnDef: ScheduleBusinessOrderDatacenterColumnDefinition;
	private organizationDatacenterColumnDef: OrganizationDatacenterColumnDefinition;
	private usersDatacenterColumnDef: UserDatacenterColumnDefinition;
	private aqdbDatacenterColumnDef: AqdbDatacenterColumnDefinition;
	private prototypeDatacenterColumnDefinition: PrototypeDatacenterColumnDefinition;
	private productListColumnDef: ProductListColumnDefinition;
	constructor(
		readonly dimensionPipe: DimensionPipe,
		readonly documentService: DocumentService,
		readonly translationService: TranslationService,
		readonly userService: UserService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {
		this.translationService.subscribeToLanguageChange({
			next: () => {
				this.ecallByBatchDatacenterColumnDef = new EcallByBatchDatacenterColumnDefinition(
					translationService
				);
				this.ecallByVolumeDatacenterColumnDef = new EcallByVolumeDatacenterColumnDefinition(
					dimensionPipe,
					translationService
				);
				this.orderDatacenterColumnDef = new OrderDatacenterColumnDefinition(
					translationService,
					userService
				);
				this.batchDatacenterColumnDef = new BatchDatacenterColumnDefinition(
					translationService,
					userService,
					documentService
				);
				this.shipmentDatacenterColumnDef = new ShipmentDatacenterColumnDefinition(translationService);
				this.qualityDatacenterColumnDef = new QualityDatacenterColumnDefinition(translationService);
				this.offerDatacenterColumnDef = new OfferDatacenterColumnDefinition(translationService);
				this.documentDatacenterColumnDef = new DocumentDatacenterColumnDefinition(
					translationService,
					documentService
				);
				this.scheduleBusinessOrderDatacenterColumnDef = new
				ScheduleBusinessOrderDatacenterColumnDefinition(translationService);
				this.organizationDatacenterColumnDef = new OrganizationDatacenterColumnDefinition(translationService);
				this.usersDatacenterColumnDef = new UserDatacenterColumnDefinition(translationService);
				this.aqdbDatacenterColumnDef = new AqdbDatacenterColumnDefinition(translationService);
				this.prototypeDatacenterColumnDefinition = new PrototypeDatacenterColumnDefinition(translationService);
				this.productListColumnDef = new ProductListColumnDefinition(
					translationService,
					productCharacteristicsTranslationService
				);
			}
		});
		this.ecallByBatchDatacenterColumnDef = new EcallByBatchDatacenterColumnDefinition(
			translationService
		);
		this.ecallByVolumeDatacenterColumnDef = new EcallByVolumeDatacenterColumnDefinition(
			dimensionPipe,
			translationService
		);
		this.orderDatacenterColumnDef = new OrderDatacenterColumnDefinition(
			translationService,
			userService
		);
		this.batchDatacenterColumnDef = new BatchDatacenterColumnDefinition(
			translationService,
			userService,
			documentService
		);
		this.shipmentDatacenterColumnDef = new ShipmentDatacenterColumnDefinition(translationService);
		this.qualityDatacenterColumnDef = new QualityDatacenterColumnDefinition(translationService);
		this.offerDatacenterColumnDef = new OfferDatacenterColumnDefinition(translationService);
		this.documentDatacenterColumnDef = new DocumentDatacenterColumnDefinition(translationService, documentService);
		this.scheduleBusinessOrderDatacenterColumnDef = new
		ScheduleBusinessOrderDatacenterColumnDefinition(translationService);
		this.organizationDatacenterColumnDef = new OrganizationDatacenterColumnDefinition(translationService);
		this.usersDatacenterColumnDef = new UserDatacenterColumnDefinition(translationService);
		this.aqdbDatacenterColumnDef = new AqdbDatacenterColumnDefinition(translationService);
		this.prototypeDatacenterColumnDefinition = new PrototypeDatacenterColumnDefinition(translationService);
		this.productListColumnDef = new ProductListColumnDefinition(
			translationService,
			productCharacteristicsTranslationService
		);
	}

	getDefaultView(datacenterType: DatacenterType) {
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			return { ...DEFAULT_VIEW_ECALL_BY_BATCH_DATACENTER };
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			return { ...DEFAULT_VIEW_ECALL_BY_VOLUME_DATACENTER };
		case 'order':
			return { ...DEFAULT_VIEW_ORDER_DATACENTER };
		case 'batch':
			return { ...DEFAULT_VIEW_BATCHES_DATACENTER };
		case 'shipment':
			return { ...DEFAULT_VIEW_SHIPMENT_DATACENTER };
		case 'offer':
			return { ...DEFAULT_VIEW_OFFER_DATACENTER };
		case 'quality':
			return { ...DEFAULT_VIEW_QUALITY_DATACENTER };
		case 'document':
			return { ...DEFAULT_VIEW_DOCUMENT_DATACENTER };
		case 'schedule-business-order':
			return { ...DEFAULT_VIEW_SCHEDULE_BUSINESS_ORDER_DATACENTER };
		case 'organization':
			return { ...DEFAULT_VIEW_ORGANIZATION_DATACENTER };
		case 'users':
			return { ...DEFAULT_VIEW_USERS_DATACENTER };
		case 'aqdb':
			return { ...DEFAULT_VIEW_AQDB_DATACENTER };
		case 'prototypes':
			return { ...DEFAULT_VIEW_PROTOTYPE_DATACENTER };
		default:
			throw new Error(`${ <string> datacenterType } datacenter type not found`);
		}
	}

	getHeaderForColumn(datacenterType: DatacenterType, coliId: string) {
		let colDef;
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			colDef = this.ecallByBatchDatacenterColumnDef.DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			colDef = this.ecallByVolumeDatacenterColumnDef.DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'order':
			this.orderDatacenterColumnDef.addExceptionReportFields();
			colDef = this.orderDatacenterColumnDef.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'batch':
			this.batchDatacenterColumnDef.addEcallBatchFields();
			colDef = this.batchDatacenterColumnDef.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'shipment':
			colDef = this.shipmentDatacenterColumnDef.SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'offer':
			colDef = this.offerDatacenterColumnDef.OFFER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'quality':
			colDef = this.qualityDatacenterColumnDef.QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'document':
			colDef = this.documentDatacenterColumnDef.DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'schedule-business-order':
			colDef = this.scheduleBusinessOrderDatacenterColumnDef
				.SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'organization':
			colDef = this.organizationDatacenterColumnDef.ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'users':
			colDef = this.usersDatacenterColumnDef.USER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'aqdb':
			colDef = this.aqdbDatacenterColumnDef.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'prototypes':
			colDef = this.prototypeDatacenterColumnDefinition.PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			return colDef ? colDef.headerName : '';
		case 'product-list':
			colDef = this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().get(coliId);
			return colDef ? colDef.headerName : '';
		default:
			throw new Error(`${ <string> datacenterType } datacenter type not found`);
		}
	}

	getIsColumnSortable(datacenterType: DatacenterType, colId: string) {
		let isColumnSortable;
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			isColumnSortable = this.ecallByBatchDatacenterColumnDef.DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			isColumnSortable = this.ecallByVolumeDatacenterColumnDef.DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'order':
			this.orderDatacenterColumnDef.addExceptionReportFields();
			isColumnSortable = this.orderDatacenterColumnDef.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'batch':
			this.batchDatacenterColumnDef.addEcallBatchFields();
			isColumnSortable = this.batchDatacenterColumnDef.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'shipment':
			isColumnSortable = this.shipmentDatacenterColumnDef.SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'offer':
			isColumnSortable = this.offerDatacenterColumnDef.OFFER_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'quality':
			isColumnSortable = this.qualityDatacenterColumnDef.QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		case 'document':
			isColumnSortable = this.documentDatacenterColumnDef.DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		case 'schedule-business-order':
			isColumnSortable = this.scheduleBusinessOrderDatacenterColumnDef
				.SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		case 'organization':
			isColumnSortable = this.organizationDatacenterColumnDef
				.ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'users':
			isColumnSortable = this.usersDatacenterColumnDef
				.USER_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : true;
		case 'aqdb':
			isColumnSortable = this.aqdbDatacenterColumnDef.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		case 'prototypes':
			isColumnSortable = this.prototypeDatacenterColumnDefinition
				.PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION.get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		case 'product-list':
			isColumnSortable = this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().get(colId);
			return isColumnSortable ? isColumnSortable.sortable : undefined;
		default:
			throw new Error(`${ <string> datacenterType } datacenter type not found`);
		}
	}

	getFieldsForColumn(datacenterType: DatacenterType, coliId: string): string[] {
		let colDef: ColDefWithFields<Record<string, unknown>> | undefined;
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			colDef = safe(<ColDefWithFields<Record<string, unknown>> | undefined> this.ecallByBatchDatacenterColumnDef
				.DEFAULT_CONFIGURATION.get(coliId));
			break;
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			colDef = safe(<ColDefWithFields<Record<string, unknown>> | undefined> this.ecallByVolumeDatacenterColumnDef
				.DEFAULT_CONFIGURATION.get(coliId));
			break;
		case 'order':
			this.orderDatacenterColumnDef.addExceptionReportFields();
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.orderDatacenterColumnDef
				.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'batch':
			this.batchDatacenterColumnDef.addEcallBatchFields();
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.batchDatacenterColumnDef
				.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'shipment':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.shipmentDatacenterColumnDef
				.SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'offer':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.offerDatacenterColumnDef
				.OFFER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'quality':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.qualityDatacenterColumnDef
				.QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'document':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.documentDatacenterColumnDef
				.DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'schedule-business-order':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined>
				this.scheduleBusinessOrderDatacenterColumnDef
					.SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'organization':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.organizationDatacenterColumnDef
				.ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'users':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.usersDatacenterColumnDef
				.USER_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'aqdb':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.aqdbDatacenterColumnDef
				.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'prototypes':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.prototypeDatacenterColumnDefinition
				.PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION.get(coliId);
			break;
		case 'product-list':
			colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.productListColumnDef
				.PRODUCT_LIST_DEFAULT_CONFIGURATION().get(coliId);
			break;
		default:
			throw new Error(`${ datacenterType } datacenter type not found`);
		}
		if (!colDef) {
			console.warn(`Column ${ coliId } not found in ${ datacenterType } datacenter type`);
			return [];
		}
		const fields = this.getMapFieldsForColumn(colDef);
		if (fields.length === 0) {
			// This colId is a group header with no fields of its own — collect fields from all child columns
			return this.getAllColumnsOfDatacenter(datacenterType)
				.flatMap(col => col.groupId === coliId && col.colId
					? this.getFieldsForColumn(datacenterType, col.colId)
					: []);
		}
		return fields;
	}

	getAllColumnsAndHeader(datacenterType: DatacenterType) {
		const columns = this.getAllColumnsOfDatacenter(datacenterType);
		const columnsAndHeader: ColumnDefAndHeader[] = [];

		for (const column of columns) {
			if (!column.colId || column.isMandatoryOnLeft) {
				continue;
			}
			columnsAndHeader.push({
				colId: column.colId,
				pinned: column.pinned,
				header: column.headerName,
				sortable: this.getIsColumnSortable(datacenterType, column.colId),
				groupId: column.groupId
			});
		}
		return columnsAndHeader;
	}

	getAllColumnsOfDatacenter(datacenterType: DatacenterType) {
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			return Array.from(this.ecallByBatchDatacenterColumnDef.DEFAULT_CONFIGURATION.values());
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			return Array.from(this.ecallByVolumeDatacenterColumnDef.DEFAULT_CONFIGURATION.values());
		case 'order':
			this.orderDatacenterColumnDef.addExceptionReportFields();
			return Array.from(this.orderDatacenterColumnDef.ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'batch':
			this.batchDatacenterColumnDef.addEcallBatchFields();
			return Array.from(this.batchDatacenterColumnDef.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'shipment':
			return Array.from(this.shipmentDatacenterColumnDef.SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'offer':
			return Array.from(this.offerDatacenterColumnDef.OFFER_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'quality':
			return Array.from(this.qualityDatacenterColumnDef.QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'document':
			return Array.from(this.documentDatacenterColumnDef.DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'schedule-business-order':
			return Array.from(this.scheduleBusinessOrderDatacenterColumnDef
				.SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'organization':
			return Array.from(this.organizationDatacenterColumnDef
				.ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'users':
			return Array.from(this.usersDatacenterColumnDef
				.USER_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'aqdb':
			return Array.from(this.aqdbDatacenterColumnDef.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'prototypes':
			return Array.from(this.prototypeDatacenterColumnDefinition
				.PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION.values());
		case 'product-list':
			return Array.from(this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().values());
		default:
			throw new Error(`${ <string> datacenterType } datacenter type not found`);
		}
	}

	getRequiredFields(datacenterType: DatacenterType) {
		switch (datacenterType) {
		case ECALL_BY_BATCH_DATACENTER_TYPE:
			return this.ecallByBatchDatacenterColumnDef.REQUIRED_FIELDS.slice();
		case ECALL_BY_VOLUME_DATACENTER_TYPE:
			return this.ecallByVolumeDatacenterColumnDef.REQUIRED_FIELDS.slice();
		case 'order':
			return this.orderDatacenterColumnDef.ORDER_REQUIRED_FIELDS.slice();
		case 'batch':
			return this.batchDatacenterColumnDef.BATCH_REQUIRED_FIELDS.slice();
		case 'shipment':
			return this.shipmentDatacenterColumnDef.SHIPMENT_REQUIRED_FIELDS.slice();
		case 'document':
			return this.documentDatacenterColumnDef.DOCUMENT_REQUIRED_FIELDS.slice();
		case 'schedule-business-order':
			return this.scheduleBusinessOrderDatacenterColumnDef.SCHEDULE_BUSINESS_ORDER_REQUIRED_FIELDS.slice();
		case 'organization':
			return this.organizationDatacenterColumnDef.ORGANIZATION_REQUIRED_FIELDS.slice();
		case 'users':
			return this.usersDatacenterColumnDef.USER_REQUIRED_FIELDS.slice();
		case 'product-list':
			return this.productListColumnDef.PRODUCT_LIST_REQUIRED_FIELDS.slice();
		default:
			return [];
		}
	}

	getMapFieldsForColumn(colDef: ColDefWithFields<Record<string, unknown>>) {
		return Array.from(new Set([...colDef.field ? [colDef.field] : [], ...colDef.fields ?? []]));
	}
}
